import { useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useApp } from "../app/AppContext.jsx";
import { specLabel } from "../i18n/index.js";
import { api, qs } from "../lib/api.js";
import { useFetch } from "../lib/useFetch.js";
import Gallery from "../components/Gallery.jsx";
import QuotePanel from "../components/QuotePanel.jsx";
import MarketNote from "../components/MarketNote.jsx";
import EnquiryForm from "../components/EnquiryForm.jsx";
import CarCard from "../components/CarCard.jsx";
import { money, km, carTitle, date, marketLabel } from "../lib/format.js";

export default function CarDetail() {
  const { id } = useParams();
  const { t, country, setCountry, lang } = useApp();
  const [options, setOptions] = useState({ freight: "container_shared", coc: false });
  const enquiryRef = useRef(null);

  const { data: car, loading, error } = useFetch(() => api.listing(id), `listing:${id}:${country}`);

  const quoteParams = useMemo(() => ({
    country,
    freight: options.freight,
    coc: options.coc ? "1" : "0",
  }), [country, options.freight, options.coc]);

  const { data: quote } = useFetch(
    () => api.quote(id, quoteParams),
    `quote:${id}${qs(quoteParams)}`,
    { skip: !car || car.priceLocal == null }
  );

  const { data: comparison } = useFetch(
    () => api.quotes(id, { freight: options.freight, coc: options.coc ? "1" : "0" }),
    `quotes:${id}:${options.freight}:${options.coc}`,
    { skip: !car || car.priceLocal == null }
  );

  if (loading) {
    return <div className="wrap page" aria-busy="true" aria-label={t("common.loading")}><div className="detail"><div><div className="gallery__main skeleton" /><div className="skeleton" style={{ height: 40, width: "75%", marginTop: 30 }} /><div className="skeleton" style={{ height: 150, marginTop: 24 }} /></div><QuotePanel /></div></div>;
  }
  if (error || !car) {
    return (
      <div className="wrap page empty">
        <p className="empty__title">{t("common.error")}</p>
        <Link className="btn btn--ghost btn--sm" to="/stock" style={{ marginTop: 20 }}>{t("common.back")}</Link>
      </div>
    );
  }

  const title = carTitle(car);
  const specs = [
    [t("car.spec.year"), car.year ? `${car.year}${car.month ? `/${String(car.month).padStart(2, "0")}` : ""}` : null],
    [t("car.spec.mileage"), car.mileageKm != null ? km(car.mileageKm, lang) : null],
    [t("car.spec.engine"), car.displacementCc ? `${(car.displacementCc / 1000).toFixed(1)} L` : null],
    [t("car.spec.fuel"), specLabel(lang, "fuel", car.fuel)],
    [t("car.spec.transmission"), specLabel(lang, "transmission", car.transmission)],
    [t("car.spec.body"), specLabel(lang, "body", car.body)],
    [t("car.spec.colour"), car.colour],
    [t("car.spec.seats"), car.seats],
    [t("car.spec.steering"), car.steering === "LHD" ? t("stock.steering.LHD") : car.steering === "RHD" ? t("stock.steering.RHD") : null],
    [t("car.spec.location"), car.location],
    [t("car.spec.seller"), specLabel(lang, "seller", car.sellerType)],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <div className="wrap page">
      <Link to="/stock" className="small muted" style={{ display: "inline-block", marginBottom: 20 }}>← {t("nav.stock")}</Link>

      {/* Above everything, including the price: a demo lot must never be mistaken for stock. */}
      {car.isDemo && (
        <div className="notice notice--danger" style={{ marginBottom: 24 }}>
          <strong>{t("car.demoLot")}</strong> — {t("car.demoLotNote")}
        </div>
      )}

      {/* A blocked car still has a page — links get shared, and whoever follows one is owed
          the reason, not a 404 and not a landed price for a car that cannot be registered. */}
      {car.importability === "no" && (
        <div className="notice notice--danger" style={{ marginBottom: 24 }}>
          <strong>{t("car.import.noTitle", { country })}</strong> — {t("car.import.noBody", { country })}
        </div>
      )}

      {car.importability === "hard" && (
        <div className="notice notice--warn" style={{ marginBottom: 24 }}>
          <strong>{t("car.import.hardTitle", { country })}</strong> — {t("car.import.hardBody")}
        </div>
      )}

      {/* Not a restriction, a certainty: every car out of Japan needs its headlights changed.
          It belongs above the fold rather than folded into "possible additional work", which
          is a list of things the car probably does not need. */}
      {car.origin === "JP" && (
        <div className="notice" style={{ marginBottom: 24 }}>
          <strong>{t("car.import.lightsTitle")}</strong> — {t("car.import.lightsBody")}
        </div>
      )}

      {car.status === "gone" && (
        <div className="notice notice--warn" style={{ marginBottom: 24 }}>
          <strong>{t("car.gone")}</strong> — {t("car.goneBody")}{" "}
          <Link to="/wanted" style={{ color: "var(--accent)" }}>{t("enq.wanted")} →</Link>
        </div>
      )}

      <div className="detail">
        <div>
          <Gallery images={car.images} alt={title} />

          <div style={{ marginTop: 30 }}>
            <div className="chips" style={{ marginBottom: 14 }}>
              <span className="badge badge--plain">{marketLabel(car.source, t)}</span>
              {car.steering === "LHD" && <span className="badge badge--ok">LHD</span>}
              {car.isOriginating && <span className="badge badge--gold">{t("car.origin")}</span>}
              {car.repairHistory === false && <span className="badge badge--ok">{t("car.noRepair")}</span>}
              {car.repairHistory === true && <span className="badge badge--warn">{t("car.repair")}</span>}
              {car.isDemo && <span className="badge badge--warn">{t("car.demoLot")}</span>}
              {/* Only when the source told us the real advertisement date, and only once it is
                  long enough to be leverage. A fresh listing is not information. */}
              {car.daysListedIsExact && car.daysListed >= 30 && (
                <span className="badge badge--plain" title={t("car.daysOnMarketHelp")}>
                  {t("car.daysOnMarket", { n: car.daysListed })}
                </span>
              )}
            </div>

            <h1 className="h1" style={{ fontSize: "clamp(24px,3.6vw,40px)" }}>{title}</h1>

            {car.priceLocal != null && (
              <p className="muted" style={{ marginTop: 12 }}>
                {t("car.listedAt", { market: marketLabel(car.source, t) })}{" "}
                <strong style={{ color: "var(--text)" }}>
                  {new Intl.NumberFormat(lang).format(car.priceLocal)} {car.currency}
                </strong>
                {car.priceEur != null && <span className="small"> · ≈ {money(car.priceEur, "EUR", country)}</span>}
              </p>
            )}
          </div>

          <h2 className="label" style={{ display: "block", margin: "34px 0 14px" }}>{t("car.specs")}</h2>
          <div className="specs">
            {specs.map(([k, v]) => (
              <div key={k} className="spec">
                <div className="spec__k">{k}</div>
                <div className="spec__v">{v}</div>
              </div>
            ))}
          </div>

          {car.auction && (
            <>
              <h2 className="label" style={{ display: "block", margin: "34px 0 14px" }}>{t("auctions.title")}</h2>
              <div className="specs">
                <div className="spec"><div className="spec__k">{t("auctions.house")}</div><div className="spec__v">{car.auction.house}</div></div>
                <div className="spec"><div className="spec__k">{t("auctions.lot")}</div><div className="spec__v">{car.auction.lotNumber}</div></div>
                <div className="spec"><div className="spec__k">{t("auctions.grade")}</div><div className="spec__v" style={{ color: "var(--accent)" }}>{car.auction.grade}</div></div>
                <div className="spec"><div className="spec__k">{t("auctions.interior")}</div><div className="spec__v">{car.auction.interiorGrade}</div></div>
                <div className="spec"><div className="spec__k">{t("auctions.saleDate")}</div><div className="spec__v">{date(car.auction.saleDate, lang)}</div></div>
              </div>
            </>
          )}

          {/* No form on a demo lot: the server refuses the enquiry anyway, and offering one
              would be a promise about a car that does not exist. The wanted list does apply. */}
          <div ref={enquiryRef} style={{ marginTop: 46, scrollMarginTop: 90 }}>
            {car.isDemo ? (
              <div className="notice notice--warn">
                {t("car.demoLotNote")}{" "}
                <Link to="/wanted" style={{ color: "var(--accent)" }}>{t("enq.wanted")} →</Link>
              </div>
            ) : (
              <>
                <p className="label">{t("enq.title")}</p>
                <h2 className="h2" style={{ margin: "12px 0 8px" }}>{t("car.enquire")}</h2>
                <p className="muted small" style={{ marginBottom: 24, maxWidth: 540 }}>{t("car.enquireSub")}</p>
                <EnquiryForm
                  kind={car.auction ? "auction_bid" : "listing"}
                  listingId={car.id}
                  freightMode={options.freight}
                />
              </>
            )}
          </div>
        </div>

        {car.priceLocal != null ? <QuotePanel
          quote={quote}
          comparison={comparison}
          options={options}
          onOptionChange={setOptions}
          onCountryChange={setCountry}
          onEnquire={car.isDemo ? null : () => enquiryRef.current?.scrollIntoView({ behavior: "smooth" })}
        /> : <div className="notice">{t("car.enquireSub")}</div>}
      </div>

      {/* Under the quote, not inside it: the quote is our arithmetic, this is the market
          answering back. Renders nothing when the sample is too thin to mean anything. */}
      <div className="detail__market">
        <MarketNote make={car.make} model={car.model} year={car.year} />
      </div>

      {car.similar?.length > 0 && (
        <section style={{ marginTop: 70 }}>
          <p className="label">{t("car.similar")}</p>
          <div className="grid grid--cars" style={{ marginTop: 20 }}>
            {car.similar.map((s) => <CarCard key={s.id} listing={s} quote={s.quote} />)}
          </div>
        </section>
      )}
    </div>
  );
}
